import React, {useEffect, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {useSnackbar} from "../../../contexts/SnackbarContext";
import {useTranslation} from "react-i18next";
import {PageTitleKeys} from "../../../config/routeConstants";
import {usePageTitle} from "../../../hooks/usePageTitle";
import {EulaAgreementResponse, termsService} from "../../../services/terms.service";
import {useContext} from "../../../contexts/Context";
import {AuthContainer} from "../Auth.styled";
import LogoWithLanguageSwitcher from "../../../components/common/LogoWithLanguageSwitcher";
import {EulaCaptionsWrapper, EulaConsentActionButtons, EulaConsentCard} from "./Eula.styled";
import {brand} from "../../../theme/brand";
import {Box, Button, Checkbox, CircularProgress, FormControlLabel, Typography} from "@mui/material";
import {EulaAgreementContent} from "../EulaDialog/EulaDialog.styled";
import {sanitizeHtml} from "../../../utils/format-data";
import {formatDateWithLanguage} from "../../../utils/format-date";
import {authService} from "../../../services/auth.service";


const Eula = () => {
    usePageTitle(PageTitleKeys.Eula);
    const {isAdminLoading} = useContext();

    const {t} = useTranslation();
    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();

    const [accepted, setAccepted] = useState(false);
    const [eulaData, setEulaData] = useState<EulaAgreementResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const fetchEulaContent = async () => {
        try {
            setIsLoading(true);
            const data = await termsService.fetchEulaAgreement();
            setEulaData(data);
        } catch (e) {
            console.error(e);
            showSnackbar(t('eula.fetchEulaError'), "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleAccept = async () => {
        if (!accepted || !eulaData) {
            showSnackbar(t('eulaDialog.missingData'), "error");
            return;
        }

        setIsSubmitting(true);
        try {
            await termsService.createEulaAcceptance(eulaData.version);
            showSnackbar(t('eulaDialog.acceptanceSuccess'), "success");

            termsService.clearPendingEulaVersion();
            navigate("/dashboard", { replace: true });
        } catch (e) {
            console.error(e);
            showSnackbar(t('eulaDialog.acceptanceError'), "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBack = () => {
        setAccepted(false);
        authService.logout();
        showSnackbar(t('eulaDialog.logoutSuccess'), "info");
        navigate("/login");
    };

    useEffect(() => {
        if (isAdminLoading) return;

        void fetchEulaContent();
    }, [isAdminLoading]);

    return (
        <AuthContainer role="region" aria-labelledby="request-eula-consent-title">
            <LogoWithLanguageSwitcher src={brand.logo} alt={t('auth.logo.alt')} clickable={true} />

            <EulaConsentCard>
                <Typography id="request-eula-consent-title" variant="h4" color="text.primary" sx={{ mb: 2 }}>
                    {t('eula.title')}
                </Typography>

                <Typography variant="subtitle1" color="text.primary">
                    {t('eula.subtitle')}
                </Typography>

                <EulaCaptionsWrapper>
                    <Typography variant="caption">
                        {t('eula.fullInfo')}
                    </Typography>
                </EulaCaptionsWrapper>

                <EulaAgreementContent>
                    {isLoading && (
                        <CircularProgress />
                    )}

                    {!isLoading && (!eulaData || eulaData.content.length === 0) && (
                        <Typography>{t('eula.noAgreement')}</Typography>
                    )}

                    {!isLoading && eulaData && eulaData.content.length > 0 && (
                        <Box sx={{ mt: 2 }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(eulaData.content) }} />
                    )}
                </EulaAgreementContent>

                {!isLoading && eulaData && (
                    <Typography variant="caption" color="text.hint">
                        {t('eula.version') + ': ' + eulaData.version} &middot; {t('eula.updateDate') + ': ' + formatDateWithLanguage(eulaData.updatedAt)}
                    </Typography>
                )}

                <FormControlLabel
                    value={accepted}
                    control={
                        <Checkbox
                            checked={(!eulaData || eulaData.content.length === 0) ? false : accepted}
                            disabled={(!eulaData || eulaData.content.length === 0)}
                            onChange={(e) => setAccepted(e.target.checked)}
                        />
                    }
                    label={t('eula.checkboxLabel')}
                    labelPlacement="end"
                    sx={{ mt: 2, gap: '8px' }}
                />

                <EulaCaptionsWrapper>
                    <Typography variant="caption" color="text.secondary">
                        {t('eula.caption')}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                        {t('eula.info')}
                    </Typography>
                </EulaCaptionsWrapper>


                <EulaConsentActionButtons>
                    <Button variant="text" onClick={handleBack}>
                        {t('common.back')}
                    </Button>

                    <Button variant="contained"
                            disabled={!accepted || (!eulaData || eulaData.content.length === 0)}
                            onClick={handleAccept}
                    >
                        {t('common.next')}
                    </Button>
                </EulaConsentActionButtons>
            </EulaConsentCard>
        </AuthContainer>
    );
};

export default Eula;